'use client';

import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle
} from '@/components/ui/card';
import Link from 'next/link';
import { useState } from 'react';

export default function ForgotPasswordPageClient() {
	const [email, setEmail] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [message, setMessage] = useState('');
	const [isSuccess, setIsSuccess] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsLoading(true);
		setMessage('');

		try {
			const response = await fetch('/api/auth/forgot-password', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ email })
			});

			const data = await response.json();

			if (response.ok) {
				setIsSuccess(true);
				setMessage(data.message);
				setEmail(''); // Clear the input field
			} else {
				setIsSuccess(false);
				setMessage(data.message || 'An error occurred');
			}
		} catch (error) {
			setIsSuccess(false);
			setMessage('An error occurred. Please try again later.');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<>
			<div className="min-h-screen flex justify-center items-start md:items-center p-8">
				<Card className="w-full max-w-sm">
					<CardHeader>
						<CardTitle className="text-2xl">Forgot Password</CardTitle>
						<CardDescription>
							Enter your email to receive a password reset link
						</CardDescription>
					</CardHeader>
					<CardContent>
						{isSuccess ? (
							<div className="text-sm">
								<p className="text-green-600 mb-4">{message}</p>
								<p>
									Please check your email for the password reset link. If you
									don't see it, check your spam folder.
								</p>
							</div>
						) : (
							<form onSubmit={handleSubmit} className="space-y-4">
								<input
									type="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder="Email"
									className="w-full h-10 px-3 mb-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
									required
								/>
								{message && <p className="text-red-500 text-sm">{message}</p>}
								<button
									type="submit"
									disabled={isLoading}
									className="w-full h-10 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded transition-colors"
								>
									{isLoading ? 'Sending...' : 'Send Reset Link'}
								</button>
							</form>
						)}
					</CardContent>
					<CardFooter>
						<p className="text-xs text-center text-gray-600 mt-4 w-full">
							Remember your password?{' '}
							<Link href="/login" className="text-blue-600 hover:underline">
								Back to login
							</Link>
						</p>
					</CardFooter>
				</Card>
			</div>
		</>
	);
}
